import { useEffect, useRef, useState } from 'react';
import { CommonService, SubscriptionService } from '../core/services';
import * as gtUtil from '../core/utility';
import { getLatestWhatsNewConfigForAudience } from '../core/utility/whatsNewConfig';
import { WhatsNewConfig, WhatsNewAudience } from '../core/interface/whats-new.interface';

const commonService = new CommonService();
const subscriptionService = new SubscriptionService();

/** Minimal identity needed to look up plan entitlements — same shape EntitlementGuard uses. */
export interface EntitlementLookupUser {
    user_id?: string | number | null;
    account_id?: string | number | null;
    influencer_id?: string | number | null;
}

// Best-effort: this announcement's feature (storefront/link-in-bio) is already
// unlocked on the influencer's active plan, so the CTA should read "Explore"
// rather than "Upgrade Now". Uses the same `getCurrentPlan` entitlements source
// as EntitlementGuard. Any failure is treated as "not yet subscribed".
const fetchAlreadySubscribed = async (user?: EntitlementLookupUser | null): Promise<boolean> => {
    if (!user?.influencer_id) return false;
    try {
        const res: any = await subscriptionService.getCurrentPlan({
            account_id: user.account_id,
            influencer_id: user.influencer_id,
            user_id: user.user_id,
            brand_id: 0,
            user_type: 'Influencer',
        });
        return (
            res?.status === gtUtil.Constants.SUCCESS &&
            !!(res?.data?.entitlements?.storefront_enabled || res?.data?.entitlements?.link_in_bio_enabled)
        );
    } catch {
        return false;
    }
};

/**
 * Asks the backend whether this account may see `popup_name` right now, using
 * the shared `popups` / `popups_view_history` tables (the same gate that has
 * driven new_platform_welcome since launch). The row's days /
 * per_day_view_limit / total_view_limit / expires_in columns are the single
 * source of truth — there is deliberately no local flag, so the budget can be
 * retuned in the DB without a release.
 *
 * IMPORTANT: this call is not side-effect free. When the backend answers "yes"
 * it immediately records a view in popups_view_history, so it must only be
 * called at the point the popup will actually be rendered — never speculatively.
 */
const checkPopup = async (
    popupName: string,
    userId?: string | number | null,
    accountId?: string | number | null
): Promise<boolean> => {
    if (!userId || !accountId) return false;
    try {
        const response: any = await commonService.checkPopup({
            user_id: userId,
            account_id: accountId,
            popup_name: popupName,
        });
        return response?.data?.status === true;
    } catch {
        // Network/backend trouble is never a reason to interrupt the user with
        // a promo — treat it as "don't show".
        return false;
    }
};

/**
 * This modal is scoped to direct influencer/brand self-serve users only.
 *
 * The URL namespace alone is not enough: an agency or talent-partner who
 * switches into a managed influencer/brand lands on '/influencer/*' or
 * '/brand/*' too, so they'd wrongly qualify. `ownerAccountType` is the OWNING
 * account (state.SwitchLayer2Reducer.switchUser.account_type), which stays
 * 'Agency'/'Talent Partner' throughout that switch — reject those outright.
 * An absent owner type means a plain self-serve login, which is allowed.
 */
export const getAccountAudienceFromPathname = (
    pathname: string,
    ownerAccountType?: string | null
): WhatsNewAudience | null => {
    const owner = (ownerAccountType || '').trim().toLowerCase();
    if (owner === 'agency' || owner === 'talent partner') return null;

    const namespace = (pathname || '').split('/').filter(Boolean)[0];
    return namespace === 'influencer' || namespace === 'brand' ? namespace : null;
};

/**
 * Resolves the announcement (if any) this user may currently be shown.
 * `audience` gates this to influencer/brand only — pass null (any other
 * section, or agency/talent-partner) to always get null back. Returns null
 * when there's nothing to show, either because no config targets this
 * audience or because the popup's view budget is spent.
 */
export const getPendingWhatsNewConfig = async (
    audience: WhatsNewAudience | null,
    userId?: string | number | null,
    accountId?: string | number | null
): Promise<WhatsNewConfig | null> => {
    if (!audience) return null;
    const latest = getLatestWhatsNewConfigForAudience(audience);
    if (!latest) return null;

    const allowed = await checkPopup(latest.popupName, userId, accountId);
    return allowed ? latest : null;
};

/**
 * Every top-level account namespace (influencer/brand/agency/talent-partner)
 * registers its own '<namespace>/settings/subscription' route — resolve the
 * right one from wherever the user currently is, so the "Upgrade" CTA always
 * lands on their own account's subscription page.
 */
export const getSubscriptionRoute = (pathname: string): string => {
    const namespace = (pathname || '').split('/').filter(Boolean)[0];
    return namespace ? `/${namespace}/settings/subscription` : '/login';
};

/**
 * Tracks whether the current user may be shown a "What's New" announcement.
 * Mounted once from ProtectedRoute so it can surface a modal on top of
 * whatever authenticated page the user happens to be on, rather than
 * interrupting navigation with a dedicated route. `audience` restricts this
 * to influencer/brand sections only — pass null everywhere else (agency,
 * talent-partner, /switch/*, etc.) to keep the hook a no-op there.
 *
 * The backend check runs at most once per signed-in identity: it records a view
 * the moment it answers "yes", so re-running it on every dependency change
 * would burn the popup's per-day / total view budget without the user seeing it
 * again. Dismissing therefore needs no write-back of its own. The guard is
 * keyed on the identity rather than being a plain "already ran" flag because
 * ProtectedRoute stays mounted across a logout/login or an account switch,
 * which has to check again.
 */
export const useWhatsNewAnnouncement = (
    audience: WhatsNewAudience | null,
    userId?: string | number | null,
    entitlementUser?: EntitlementLookupUser | null
) => {
    const [config, setConfig] = useState<WhatsNewConfig | null>(null);
    const [alreadySubscribed, setAlreadySubscribed] = useState(false);
    const checkedIdentityRef = useRef<string | null>(null);
    const accountId = entitlementUser?.account_id;

    useEffect(() => {
        if (!audience || !userId || !accountId) return;
        const identity = `${audience}:${userId}:${accountId}`;
        if (checkedIdentityRef.current === identity) return;
        checkedIdentityRef.current = identity;

        let cancelled = false;
        getPendingWhatsNewConfig(audience, userId, accountId).then((pending) => {
            if (!cancelled) setConfig(pending);
        });
        return () => { cancelled = true; };
    }, [audience, userId, accountId]);

    // Only relevant for announcements gated behind a subscription — skip the
    // lookup entirely otherwise.
    useEffect(() => {
        if (!config?.requiresSubscription) {
            setAlreadySubscribed(false);
            return;
        }
        let cancelled = false;
        fetchAlreadySubscribed(entitlementUser).then((subscribed) => {
            if (!cancelled) setAlreadySubscribed(subscribed);
        });
        return () => { cancelled = true; };
    }, [config, entitlementUser?.user_id, entitlementUser?.account_id, entitlementUser?.influencer_id]);

    // Closes immediately — the view was already recorded backend-side when the
    // popup was resolved, so there is nothing to persist here.
    const dismiss = (): void => setConfig(null);

    return { config, dismiss, alreadySubscribed };
};
